import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { getTransactionReportSearchData } from "./../../stores/services/report.service";
import { getFundByAmc } from "../../stores/services/funds.service";
import moment from "moment";
import { BsTable } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import { PDFExport } from "@progress/kendo-react-pdf";
import ReportHeader from "./report-header";
import { numberWithCommas } from "../../utils/customFunction";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import Select from "react-select";
import ReportTemplates from "../../components/ReportTemplate/ReportTemplate";

// Constants

const MONTH_NAMES = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const IN_TXN_TYPES = ["saleofunit", "conversionin"];

const STATES_COLUMNS = [
  { label: "Transfer Date", key: "transferDate" },
  { label: "Month", key: "month" },
  { label: "Year", key: "year" },
  { label: "AMC Code", key: "amcCode" },
  { label: "Fund ID", key: "fundId" },
  { label: "Total IN Transactions", key: "totalInCount" },
  { label: "Total Units IN", key: "totalUnitsIn" },
  { label: "Total Value IN ", key: "totalValueIn" },
  { label: "Total OUT Transactions", key: "totalOutCount" },
  { label: "Total Units OUT", key: "totalUnitsOut" },
  { label: "Total Value OUT ", key: "totalValueOut" },
  { label: "GAIN/LOSS", key: "gainLoss" },
  { label: "CGT Amount", key: "totalCGT" },
];

// Types
interface StatesRow {
  transferDate: string;
  month: string;
  year: string;
  amcCode: string;
  fundId: string;
  totalInCount: number;
  totalUnitsIn: number;
  totalValueIn: number;
  totalOutCount: number;
  totalUnitsOut: number;
  totalValueOut: number;
  gainLoss: number;
  totalCGT: number;
}

// Helper
const computeStatesRow = (
  reportTxn: any[],
  amcCode: string,
  fundCode: string,
): StatesRow => {
  const firstTxn = reportTxn?.[0];
  const transferDateMoment = moment(firstTxn?.created_at ?? "");
  const transferDate = transferDateMoment.isValid()
    ? transferDateMoment.format("D/M/YYYY")
    : String(firstTxn?.created_at ?? "");
  const month = transferDateMoment.isValid()
    ? MONTH_NAMES[transferDateMoment.month()]
    : "";
  const year = transferDateMoment.isValid()
    ? transferDateMoment.format("YYYY")
    : "";

  let totalInCount = 0,
    totalUnitsIn = 0,
    totalValueIn = 0;
  let totalOutCount = 0,
    totalUnitsOut = 0,
    totalValueOut = 0;
  let totalCGT = 0;

  reportTxn.forEach((txn: any) => {
    const isIn = IN_TXN_TYPES.includes(txn?.txnr_type);
    const units = parseFloat(txn?.units || "0");
    const nav = parseFloat(txn?.nav || "0");
    const value = units * nav;
    const cgt = parseFloat(txn?.tax_cgt || "0");

    if (isIn) {
      totalInCount += 1;
      totalUnitsIn += units;
      totalValueIn += value;
    } else {
      totalOutCount += 1;
      totalUnitsOut += units;
      totalValueOut += value;
    }
    totalCGT += cgt;
  });

  return {
    transferDate,
    month,
    year,
    amcCode: amcCode,
    fundId: fundCode,
    totalInCount,
    totalUnitsIn,
    totalValueIn,
    totalOutCount,
    totalUnitsOut,
    totalValueOut,
    gainLoss: 0,
    totalCGT,
  };
};

// Component
const TransactionReportStates = (props: any) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const pdfExportComponent = React.useRef<PDFExport>(null);
  const history = useHistory();

  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);
  const email = sessionStorage.getItem("email") || "";
  const amc_code = sessionStorage.getItem("amc_code") || "";
  const amc_name = sessionStorage.getItem("amc_name") || "";
  let date = new Date();

  // Filter state
  const [fund_code, setFund_code] = useState<any>(
    JSON.parse(sessionStorage.getItem("fund_code") || "[]"),
  );
  const [allFunds, setAllFunds] = useState<any>([]);
  const [fields, setFields] = useState({
    amc_code,
    fund_code: "",
    from_date: moment(date).format("YYYY-MM-DD"),
    to_date: moment(date).format("YYYY-MM-DD"),
    txn_type: "saleofunit,redemptionofunits,conversionout,conversionin",
    status: "COMPLETED",
  });

  const [loading, setLoading] = useState(false);
  const [isSearchTrue, setIsSearchTrue] = useState(false);
  const [statesRow, setStatesRow] = useState<StatesRow | null>(null);

  const [snapFund, setSnapFund] = useState("");
  const [snapFromDate, setSnapFromDate] = useState("");
  const [snapToDate, setSnapToDate] = useState("");

  const [fontSizeVal, setFontSizeVal] = useState<any>(18);
  const [paperSizeVal, setPaperSizeVal] = useState("A4");
  const [layoutVal, setLayoutVal] = useState("Landscape");
  const [marginPDF, setMarginPDF] = useState("1cm");
  const [scalePDF, setScalePDF] = useState(0.5);
  const [screenSizePerPdf, setScreenSizePerPdf] = useState<any>();
  const [dimensionOfPages, setDimensionOfPages] = useState<any>();

  // Effects
  React.useEffect(() => {
    const stored = sessionStorage.getItem("fund_code");
    setFund_code(stored ? JSON.parse(stored) : undefined);
  }, []);

  React.useEffect(() => {
    const handleStorageChange = () => {
      const fundCode = sessionStorage.getItem("fund_code");
      setFund_code(fundCode ? JSON.parse(fundCode) : undefined);
    };
    handleStorageChange();
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const getAllAmcFunds = async () => {
    try {
      const response = await getFundByAmc(
        email,
        amc_code,
        fund_code?.length > 0 ? fund_code : "",
      );
      setAllFunds(response.data.data);
    } catch (err: any) {
      toast.error(
        err.response !== undefined
          ? err.response.data.message
          : "Request Failed!",
      );
    }
  };

  React.useEffect(() => {
    getAllAmcFunds();
  }, [fund_code]);

  useEffect(() => {
    const elements = document.querySelectorAll(
      "#states-test",
    ) as NodeListOf<HTMLElement>;
    elements.forEach((el) => {
      el.style.setProperty("font-size", `${fontSizeVal}px`, "important");
    });
  }, [fontSizeVal, isSearchTrue]);

  // Drop-down helpers
  const renderFundsDropDown = () =>
    allFunds?.map((item: any) => ({
      value: item.symbol_code,
      label: `${item.fund_name} - ${item.symbol_code}`,
    }));

  const selectedFundOption = React.useMemo(() => {
    const found =
      allFunds?.length > 0 &&
      allFunds?.find((item: any) => item?.symbol_code === fields["fund_code"]);
    return found
      ? {
          value: found.symbol_code,
          label: `${found.fund_name} - ${found.symbol_code}`,
        }
      : null;
  }, [allFunds, fields["fund_code"]]);

  // customStyles
  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (styles: any, { isFocused, isSelected }: any) => ({
      ...styles,
      background: isFocused
        ? "hsla(291, 64%, 42%, 0.5)"
        : isSelected
          ? "hsla(291, 64%, 42%, 1)"
          : undefined,
      zIndex: 1,
    }),
    menuList: (styles: any) => ({
      ...styles,
      background: theme === "dark" ? "#3b3b3b" : "#ffffff",
      color: theme === "dark" ? "white" : "",
    }),
    menu: (base: any) => ({ ...base, zIndex: 100 }),
    control: (base: any, state: any) => ({
      ...base,
      background: theme === "dark" ? "#25273a" : "#f2f2f2",
      borderRadius: "0.4285rem",
      borderColor: state.isFocused
        ? "#1d8cf8"
        : theme !== "light"
          ? "#2b3553"
          : "",
      boxShadow: null,
      "&:hover": { borderColor: state.isFocused ? "" : "#ba54f5" },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "white" : "black",
    }),
    input: (base: any) => ({
      ...base,
      color: theme === "dark" ? "white" : "black",
    }),
  };

  // Search
  const searchData = async () => {
    setIsSearchTrue(false);
    setLoading(true);
    setStatesRow(null);

    try {
      const searchResponse = await getTransactionReportSearchData(
        email,
        fields,
      );

      const rawData: any[] = searchResponse.data.data ?? [];

      setSnapFund(fields["fund_code"]);
      setSnapFromDate(fields["from_date"]);
      setSnapToDate(fields["to_date"]);

      if (rawData.length === 0) {
        setIsSearchTrue(false);
        setStatesRow(null);
        toast.error("No data found");
        return;
      }

      const row = computeStatesRow(rawData, amc_code, fields["fund_code"]);

      if (!row) {
        setIsSearchTrue(false);
        setStatesRow(null);
        toast.error("No data found");
        return;
      }

      setStatesRow(row);
      setIsSearchTrue(true);
      setFontSizeVal(18);
    } catch (err: any) {
      toast.error(
        err.response !== undefined
          ? err.response.data.message
          : "Request Failed!",
      );
    } finally {
      setLoading(false);
    }
  };
  // Format helpers
  const fmt4 = (n: number) => numberWithCommas(n.toFixed(4));

  const getCellValue = (row: StatesRow, key: string): string => {
    switch (key) {
      case "transferDate":
        return row.transferDate;
      case "month":
        return row.month;
      case "year":
        return row.year;
      case "amcCode":
        return row.amcCode;
      case "fundId":
        return row.fundId;
      case "totalInCount":
        return row.totalInCount.toString();
      case "totalUnitsIn":
        return fmt4(row.totalUnitsIn);
      case "totalValueIn":
        return fmt4(row.totalValueIn);
      case "totalOutCount":
        return row.totalOutCount.toString();
      case "totalUnitsOut":
        return fmt4(row.totalUnitsOut);
      case "totalValueOut":
        return fmt4(row.totalValueOut);
      case "gainLoss":
        return fmt4(row.gainLoss);
      case "totalCGT":
        return fmt4(row.totalCGT);
      default:
        return "";
    }
  };

  // Excel export
  const handleDownloadExcel = () => {
    if (!statesRow) return;

    const headers = STATES_COLUMNS.map((c) => c.label);
    const dataRow = STATES_COLUMNS.map((c) => getCellValue(statesRow, c.key));

    const sheetData = [headers, dataRow];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Column widths
    ws["!cols"] = headers.map(() => ({ wch: 20 }));

    XLSX.utils.book_append_sheet(wb, ws, "States");
    XLSX.writeFile(wb, "States-Report.xlsx");
  };

  // TXT export
  const downloadTxtBlob = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTXT = () => {
    if (!statesRow) return;

    let content = "";

    const rawRow = STATES_COLUMNS.map((c) => {
      const val = getCellValue(statesRow, c.key);
      return `${val.replace(/,/g, "")}`;
    });
    content += rawRow.join(",") + "\n";

    downloadTxtBlob(content, "States-Report.txt");
  };

  const handleGeneratePdf = () => {
    const element = contentRef.current;
    html2pdf()
      .from(element)
      .set({
        filename: "States-Report.pdf",
        margin: [10, 3, 10, 3],
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 1 },
        jsPDF: {
          unit: "mm",
          format: paperSizeVal || "A4",
          orientation: layoutVal === "Landscape" ? "landscape" : "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .save();
  };

  // Render
  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Container fluid>
              <ToastContainer limit={1} />

              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">States Report</h4>
                </div>
                <div className="card-body">
                  <Row>
                    <Col md="4">
                      <div className="form-group">
                        <label>Select Fund</label>
                        <Select
                          value={selectedFundOption}
                          onChange={(e: any) => {
                            setFields({ ...fields, fund_code: e?.value ?? "" });
                            setStatesRow(null);
                          }}
                          menuPortalTarget={document.body}
                          isClearable
                          isSearchable
                          options={renderFundsDropDown()}
                          styles={customStyles}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="form-group">
                        <label>Settlement Date From</label>
                        <input
                          type="date"
                          value={fields["from_date"]}
                          className="form-control py-1"
                          onChange={(e) => {
                            setFields({ ...fields, from_date: e.target.value });
                            setStatesRow(null);
                          }}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="form-group">
                        <label>Settlement Date To</label>
                        <input
                          type="date"
                          value={fields["to_date"]}
                          className="form-control py-1"
                          onChange={(e) => {
                            setFields({ ...fields, to_date: e.target.value });
                            setStatesRow(null);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <button
                    className="btn btn-primary mt-3"
                    onClick={searchData}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" />
                        <span> Loading...</span>
                      </>
                    ) : (
                      "Search"
                    )}
                  </button>
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-body">
                  {statesRow && (
                    <Row>
                      <ReportTemplates
                        ColumnArr={STATES_COLUMNS}
                        selectedValues={{
                          heading: "States Report",
                          columns: STATES_COLUMNS.map((c) => c.label),
                        }}
                        pdfExportComponent={pdfExportComponent}
                        setFontSizeVal={setFontSizeVal}
                        fontSizeVal={fontSizeVal}
                        setPaperSizeVal={setPaperSizeVal}
                        paperSizeVal={paperSizeVal}
                        setLayoutVal={setLayoutVal}
                        layoutVal={layoutVal}
                        setScalePDF={setScalePDF}
                        scalePDF={scalePDF}
                        setMarginPDF={setMarginPDF}
                        marginPDF={marginPDF}
                        setScreenSizePerPdf={setScreenSizePerPdf}
                        screenSizePerPdf={screenSizePerPdf}
                        setDimensionOfPages={setDimensionOfPages}
                        contentRef={contentRef}
                        fileName="States-Report.pdf"
                        printExcel={handleDownloadExcel}
                        printTXT={handleTXT}
                        printpdf={handleGeneratePdf}
                      />
                    </Row>
                  )}

                  <div className="mt-3">
                    {loading && (
                      <div className="text-center p-4">
                        <span className="spinner-border" />
                        <p>Loading data...</p>
                      </div>
                    )}

                    {!loading && !statesRow && (
                      <div className="text-center p-4 text-muted">
                        <BsTable size={40} />
                        <p className="mt-2">
                          Select filters and click <b>Search</b>
                        </p>
                      </div>
                    )}

                    {!loading && statesRow && (
                      <>
                        <ReportHeader title="States Report" />

                        <table className="table mt-3">
                          <thead>
                            <tr>
                              {STATES_COLUMNS.map((col, i) => (
                                <th key={i}>{col.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              {STATES_COLUMNS.map((col, i) => (
                                <td key={i}>
                                  {getCellValue(statesRow, col.key)}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>

                        <div className="row mt-4">
                          <div className="col-md-4">
                            <b>Total IN Transactions:</b>{" "}
                            {statesRow.totalInCount}
                          </div>
                          <div className="col-md-4">
                            <b>Total OUT Transactions:</b>{" "}
                            {statesRow.totalOutCount}
                          </div>
                          <div className="col-md-4">
                            <b>CGT:</b> {statesRow.totalCGT}
                          </div>
                        </div>

                        <div className="text-end mt-3">
                          <span className="badge bg-secondary">
                            Total record: 1
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Container>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default TransactionReportStates;